import { type ResultOf } from '@graphql-typed-document-node/core'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  RichFilterGroupsDisplay,
  type RichFilterGroupsDisplayProps,
} from '~/components/features/RichFilterGroupsDisplay'
import { graphql } from '~/gql'
import {
  buildSurveyGroupFilterTextGroups,
  type SurveyGroupFilterGroups,
} from '~/utils'

const surveyGroupQuestionFragment = graphql(`
  fragment RichSurveyGroupFilterGroupsDisplay_SurveyGroupQuestion on SurveyGroupQuestion {
    ...BuildSurveyGroupFilterTextGroups_SurveyGroupQuestion
  }
`)

const surveyGroupBrandFragment = graphql(`
  fragment RichSurveyGroupFilterGroupsDisplay_SurveyGroupBrand on SurveyGroupBrand {
    ...BuildSurveyGroupFilterTextGroups_SurveyGroupBrand
  }
`)

const surveyGroupCustomerAttributeGroupFragment = graphql(`
  fragment RichSurveyGroupFilterGroupsDisplay_SurveyGroupCustomerAttributeGroup on SurveyGroupCustomerAttributeGroup {
    ...BuildSurveyGroupFilterTextGroups_SurveyGroupCustomerAttributeGroup
  }
`)

export type RichSurveyGroupFilterGroupsDisplayProps = {
  brands: ResultOf<typeof surveyGroupBrandFragment>[]
  customerAttributeGroups: ResultOf<
    typeof surveyGroupCustomerAttributeGroupFragment
  >[]
  'data-testid'?: string
  filterGroups: SurveyGroupFilterGroups
  questions: ResultOf<typeof surveyGroupQuestionFragment>[]
}

export const RichSurveyGroupFilterGroupsDisplay = ({
  brands,
  customerAttributeGroups,
  'data-testid': dataTestId = 'components-features-rich-survey-group-filter-groups-display',
  filterGroups,
  questions,
}: RichSurveyGroupFilterGroupsDisplayProps) => {
  const { t } = useTranslation()
  const filterTextGroups = React.useMemo<
    RichFilterGroupsDisplayProps['filterTextGroups']
  >(
    () =>
      buildSurveyGroupFilterTextGroups({
        filterGroups,
        questions,
        customerAttributeGroups,
        brands,
        t,
      }),
    [brands, customerAttributeGroups, filterGroups, questions, t],
  )

  return (
    <RichFilterGroupsDisplay
      data-testid={dataTestId}
      filterTextGroups={filterTextGroups}
    />
  )
}
